import {
  Stack,
  styled,
  Typography,
  LinearProgress,
  Collapse
} from '@mui/material';
import DownloadingOutlinedIcon from '@mui/icons-material/DownloadingOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAppSelector } from 'src/app/hooks';
import { useEffect, useMemo, useState } from 'react';
import { INotification } from 'src/models/general';
import { useQuery } from '@apollo/client';
import { CHECK_NOTIFICATION } from 'src/services/graphql/notification/query';
import { FirstTimeTour } from 'src/constants/productTour/FirstTimeTour';

const CustomizedProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'isSuccess'
})<{ isSuccess: boolean }>(
  ({ isSuccess }) =>
    `
  background-color: #f5f5f5;
  & .MuiLinearProgress-barColorPrimary {
    background-color: ${isSuccess ? '#10B981' : '#285771'};
  }
`
);

const PopulatingDataProgressV2 = () => {
  const projectId = useAppSelector((state) => state.users.project.id);
  const isFromOnboarding = localStorage.getItem('isFromOnboarding');
  const [show, setShow] = useState<boolean>(!!isFromOnboarding);
  const [objectProgresses, setObjectProgresses] = useState<INotification[]>([]);

  const {
    isOnTour,
    currentTour: { stepNumber, menu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourFirstTimeStep2 =
    isOnTour && stepNumber === 2 && menu === FirstTimeTour.menu;

  // Read from cache only - data populated by Activity component
  const { data } = useQuery(CHECK_NOTIFICATION, {
    fetchPolicy: 'cache-first',
    variables: { projectId },
    onCompleted: (response) => {
      if (!response) return;

      const OBJECT_ACTIVITY_TYPES = new Set([
        'firstTimeSocialMediaData',
        'copyKeyword'
      ]);

      const currentProgresses: INotification[] =
        response.notification_getNotification;

      const objectProgresses = currentProgresses.filter((activity) =>
        OBJECT_ACTIVITY_TYPES.has(activity.subTypeNotification)
      );
      setObjectProgresses(objectProgresses);
    },
    pollInterval: 10 * 1000,
    skip: !show
  });

  const successObjectProgresses = useMemo(() => {
    if (!objectProgresses.length) return [];
    return objectProgresses.filter((activity) => activity.currentProgress > 0);
  }, [objectProgresses]);

  const progressValue = useMemo(() => {
    if (!successObjectProgresses.length) return 0;
    return Math.min(
      (successObjectProgresses.length / objectProgresses.length) * 100
    );
  }, [objectProgresses, successObjectProgresses]);

  const isSuccess = progressValue === 100;

  // only show if it's from onboarding page
  useEffect(() => {
    if (!isFromOnboarding) {
      setShow(false);
    }
    if (isSuccess) localStorage.removeItem('isFromOnboarding');
  }, []);

  return (
    <Collapse in={show}>
      <Stack direction="row" alignItems="center" gap={1}>
        {isSuccess ? (
          <CheckCircleIcon sx={{ fontSize: '36px', color: '#10B981' }} />
        ) : (
          <DownloadingOutlinedIcon
            sx={{ fontSize: '36px', color: '#285771' }}
          />
        )}

        <Stack gap={1}>
          <Stack direction="row" alignItems="end" gap={1}>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: isSuccess ? '#10B981' : 'inherit'
              }}
            >
              {progressValue.toFixed()}%
            </Typography>
            <Typography variant="subtitle2" color="#2A8A55">
              {isSuccess ? 'Data complete' : 'Processing your data'}
            </Typography>
          </Stack>
          <CustomizedProgress
            value={progressValue}
            variant={data ? 'determinate' : 'indeterminate'}
            isSuccess={isSuccess}
          />
        </Stack>
      </Stack>
    </Collapse>
  );
};

export default PopulatingDataProgressV2;
